import { useState, useEffect, useCallback, useRef } from 'react';
import { getCached, setCache, invalidate } from '@/utils/cache';

// Default stale time: 60 seconds - serve cached data instantly, revalidate in background
const DEFAULT_STALE_MS = 60_000;

/**
 * Hook for cached data fetching with stale-while-revalidate strategy.
 * - Returns cached data instantly (no loading flash on revisit)
 * - If cached data is older than staleMs, revalidates in background
 * - refresh() keeps previous data mounted during fetch
 */
export function useCachedData(cacheKey, fetcher, { deps = [], staleMs = DEFAULT_STALE_MS } = {}) {
  const cached = getCached(cacheKey);
  const [data, setData] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(async () => {
    invalidate(cacheKey);
    setError(null);
    try {
      const result = await fetcherRef.current();
      setCache(cacheKey, result);
      setData(result);
    } catch (err) {
      console.error(`[cache] ${cacheKey} fetch error:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [cacheKey]);

  useEffect(() => {
    const existing = getCached(cacheKey);
    if (existing) {
      setData(existing);
      setLoading(false);
      // Stale-while-revalidate: if cache entry is old, refresh in background
      const entry = getCached.__store?.get(cacheKey);
      if (entry && staleMs !== Infinity && Date.now() - entry.ts > staleMs) {
        fetcherRef.current()
          .then(result => { setCache(cacheKey, result); setData(result); })
          .catch(() => {});
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef.current()
      .then(result => {
        if (cancelled) return;
        setCache(cacheKey, result);
        setData(result);
      })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ...deps]);

  return { data, loading, error, refresh };
}
